import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import { runContentStrategy } from "src/common/Scraper";
import { KeywordIntelligenceRunPayload } from "../../types/keyword-assist.type";
import { sleep } from "src/common/config/FunctionHelper";
import useUpdateUserAgents from "../../hooks/useUpdateUserAgents";

interface UseContentStrategyMutationProps {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
}

export function useContentStrategyMutation({
  onSuccess,
  onError,
  onMutateStart
}: UseContentStrategyMutationProps = {}) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents } = useUpdateUserAgents('content-strategy');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<string[]>([]);

  const runContentStrategyMutation = useMutation({
    mutationFn: (payload: KeywordIntelligenceRunPayload) =>
      runContentStrategy(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["content-strategy", variables?.user_id, variables?.site_id],
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      if (onError) {
        onError(error);
      }

      handleAgentStatusUpdate(false, variables?.site_id, variables?.competitor_keywords)
    },
  });

  function handleAgentStatusUpdate(status: boolean, overrideSiteId?: string, competitorKeywords?: string[]) {
    updateAgents({
      site_id: overrideSiteId,
      agents: [
        {
          name: 'content_strategy',
          enabled: status,
          config: {
            competitors: competitorKeywords || keywords
          }
        }
      ]
    });
  }

  const handleEnableContentStrategy = async (kws: string[], overrides: { siteId: string; userId: string, shouldRun?: boolean }) => {
    const effectiveSiteId = overrides?.siteId;
    const effectiveUserId = overrides?.userId;
    const shouldRun = overrides?.shouldRun ?? true;

    if (onMutateStart) {
      onMutateStart();
    }
    setTimeout(() => {
      setDialogOpen(false);
    }, 2000);

    handleAgentStatusUpdate(true, overrides?.siteId, kws)

    await sleep(1000);

    if(shouldRun){
      runContentStrategyMutation.mutate({
        stream: false,
        site_id: effectiveSiteId,
        user_id: effectiveUserId,
        competitor_keywords: kws,
      });
    }
  };

  return {
    dialogOpen,
    setDialogOpen,
    keywords,
    setKeywords,
    handleEnableContentStrategy,
    runContentStrategyMutation,
  };
}
